/**
 * The three records the office raises against a customer — a deal, a fault, or
 * a service request — in two presentations that share one item list:
 *
 *   CustomerCreateMenu    — a "New ▾" dropdown for the customer header, where
 *                           space is tight and this is one action among many.
 *   CustomerCreateActions — the same three as inline cards, for empty states
 *                           where the whole point is to show what's available.
 *
 * The menu replaces the flat row of per-record pills in CustomerDetailModal's
 * header: six side-by-side actions wrapped onto two lines and gave the two
 * that actually create work the same weight as `mailto:`. Icons + colours
 * mirror CustomerHistoryTimeline's KIND_META so the chooser reads the same as
 * the timeline the new record lands in.
 *
 * Presentation only — the host owns the intake modals and passes a launcher.
 * `onOpenChange` exists so the host can suspend its own Escape-to-close while
 * the dropdown is open (useModalAccessibility's `escapeDisabled`); without it
 * a single Escape closes the menu AND the customer record behind it.
 */

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ChevronDown, Zap, Briefcase, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type CustomerCreateAction = 'deal' | 'fault' | 'service-request';

interface MenuItem {
  action: CustomerCreateAction;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Icon tile — background + border + colour, one accent per record type. */
  tile: string;
}

const ITEMS: MenuItem[] = [
  {
    action: 'deal',
    label: 'Deal',
    description: 'Quote, new install or an upgrade',
    icon: Briefcase,
    tile: 'bg-emerald-500/15 border-emerald-500/25 text-emerald-500 dark:text-emerald-400',
  },
  {
    action: 'fault',
    label: 'Fault',
    description: 'Something is broken, down or degraded',
    icon: Zap,
    tile: 'bg-rose-500/15 border-rose-500/25 text-rose-500 dark:text-rose-400',
  },
  {
    action: 'service-request',
    label: 'Service request',
    description: 'Plan change, relocation or account update',
    icon: FileText,
    tile: 'bg-indigo-500/15 border-indigo-500/25 text-indigo-500 dark:text-indigo-400',
  },
];

/**
 * The three record types as inline cards. Use in empty states, where the
 * options should be visible rather than hidden behind a trigger — a dropdown
 * anchored inside a scroll container also gets clipped when the viewport is
 * short (measured: 103px of a 182px menu below the fold at 640px tall).
 */
export function CustomerCreateActions({
  onSelect,
}: {
  onSelect: (action: CustomerCreateAction) => void;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {ITEMS.map((item) => {
        const Icon = item.icon;
        return (
          <button
            key={item.action}
            type="button"
            onClick={() => onSelect(item.action)}
            className="w-[220px] flex items-start gap-2.5 px-3 py-2.5 rounded-xl border border-theme bg-surface-glass text-left hover:bg-surface-hover hover:border-brand-secondary/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/50 transition-colors"
          >
            <span
              className={`mt-0.5 w-7 h-7 shrink-0 rounded-lg border flex items-center justify-center ${item.tile}`}
            >
              <Icon className="w-3.5 h-3.5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-bold text-theme-primary">{item.label}</span>
              <span className="block text-[11px] leading-snug text-theme-tertiary">
                {item.description}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface CustomerCreateMenuProps {
  /** Launcher per record type. Called after the menu closes. */
  onSelect: (action: CustomerCreateAction) => void;
  /** Shown as the menu's caption so the operator can see what they're
   *  creating against — matters when they've drilled into a child site. */
  customerName?: string;
  /** Fires on open/close so the host can suspend Escape-to-close. */
  onOpenChange?: (open: boolean) => void;
}

export function CustomerCreateMenu({
  onSelect,
  customerName,
  onOpenChange,
}: CustomerCreateMenuProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuId = useId();

  const setOpenState = useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  // Close on outside click. mousedown (not click) so the menu is gone before
  // whatever was clicked underneath handles its own press.
  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpenState(false);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, setOpenState]);

  // Move focus into the menu on open so keyboard users land on the first item.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => itemRefs.current[0]?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const close = useCallback(
    (returnFocus: boolean) => {
      setOpenState(false);
      if (returnFocus) triggerRef.current?.focus();
    },
    [setOpenState],
  );

  const choose = (action: CustomerCreateAction) => {
    setOpenState(false);
    onSelect(action);
  };

  /**
   * Roving focus across the items — standard menu keyboard contract.
   *
   * Bound to the WRAPPER, not the menu panel, so it also catches keys while
   * focus sits on the trigger (Shift+Tab back onto it is one press away).
   * Panel-only binding left Escape unhandled there, and the host suspends its
   * own Escape while we're open — so the menu was unclosable by keyboard.
   */
  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!open) return;

    const focusAt = (index: number) => {
      const count = ITEMS.length;
      itemRefs.current[((index % count) + count) % count]?.focus();
    };
    const current = itemRefs.current.findIndex((el) => el === document.activeElement);

    switch (e.key) {
      case 'Escape':
        // Stops the host modal's document-level Escape handler from also
        // firing. Belt and braces alongside `onOpenChange` → escapeDisabled.
        e.preventDefault();
        e.stopPropagation();
        close(true);
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusAt(current + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusAt(current - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusAt(0);
        break;
      case 'End':
        e.preventDefault();
        focusAt(ITEMS.length - 1);
        break;
      case 'Tab':
        // Tab dismisses the menu. preventDefault + focus the trigger rather
        // than letting the browser advance: the next focusable in DOM order
        // is a menu item that is about to unmount, which strands focus on
        // <body>. From the trigger the user's next Tab continues normally.
        e.preventDefault();
        close(true);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative" onKeyDown={handleMenuKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close(false) : setOpenState(true))}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpenState(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title="Create a deal, fault or service request for this customer"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-secondary/50 bg-brand-secondary text-brand-dark hover:brightness-110 shadow-lg shadow-brand-secondary/20 ${
          open ? 'brightness-110' : ''
        }`}
      >
        <Plus className="w-3.5 h-3.5" />
        New
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="menu"
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label="Create a new record for this customer"
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.14, ease: 'easeOut' }}
            // z-30 clears the tab strip + content region beneath the header.
            className="absolute top-full right-0 mt-2 z-30 w-[290px] max-w-[calc(100vw-3rem)] origin-top rounded-xl border border-theme bg-surface-card shadow-2xl p-1.5"
          >
            <p className="px-2.5 pt-1 pb-2 text-[10px] font-bold uppercase tracking-wider text-theme-muted truncate">
              {customerName ? `New for ${customerName}` : 'Create new'}
            </p>
            {ITEMS.map((item, index) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.action}
                  ref={(el) => {
                    itemRefs.current[index] = el;
                  }}
                  type="button"
                  role="menuitem"
                  onClick={() => choose(item.action)}
                  className="w-full flex items-start gap-2.5 px-2.5 py-2 rounded-lg text-left hover:bg-surface-hover focus:bg-surface-hover focus:outline-none transition-colors"
                >
                  <span
                    className={`mt-0.5 w-7 h-7 shrink-0 rounded-lg border flex items-center justify-center ${item.tile}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-xs font-bold text-theme-primary">
                      {item.label}
                    </span>
                    <span className="block text-[11px] leading-snug text-theme-tertiary">
                      {item.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
